/** Shared English catalogue (namespace `common`). Apps add their own namespaces for page copy. */
const en = {
  app: {
    name: "National Policy AI",
    fullName: "UAE National Policy AI",
    owner: "General Secretariat of the Cabinet",
    ownerFull: "General Secretariat of the Cabinet · United Arab Emirates",
    government: "United Arab Emirates Government",
    consoleName: "Platform Console",
    portalName: "Public Consultation Portal",
    description: "UAE National Policy AI - the federal platform for developing, reviewing and evaluating public policy",
    consoleDescription: "UAE National Policy AI - Platform Console for tenants, identity, workflows, agents and AI governance",
    portalDescription: "Public consultations on federal policies of the United Arab Emirates",
    skipToContent: "Skip to main content",
    copyright: "© {{year}} General Secretariat of the Cabinet, United Arab Emirates",
  },
  language: {
    switchTo: "العربية",
    current: "English",
    label: "Language",
  },
  nav: {
    main: "Main menu",
    home: "Home",
    commandCenter: "Command Center",
    nationalCommandCenter: "National Command Center",
    policies: "Policies",
    policyRegister: "Policy register",
    pipelineBoard: "Pipeline board",
    newPolicy: "New policy",
    studio: "Policy Studio",
    startPolicy: "Start a policy",
    ideas: "Ideas",
    opportunities: "Policy opportunities",
    directory: "Policymaker directory",
    benchmarkWatch: "Benchmark watch",
    requirementPacks: "Requirement packs",
    consultation: "Consultation",
    consultationRounds: "Consultation rounds",
    interMinisterial: "Inter-ministerial requests",
    monitoring: "Monitoring",
    indicators: "Indicators",
    evaluations: "Evaluations",
    reglab: "RegLab pilots",
    signals: "Signals",
    knowledge: "Knowledge",
    legislation: "Legislation and policies",
    dataIndicators: "Data and indicators",
    benchmarks: "Benchmarks",
    academy: "Academy",
    userGuide: "User guide",
    registry: "National registry",
    settings: "Settings",
    members: "Members",
    roles: "Roles",
    portfolio: "Portfolio profile",
    governance: "Governance",
    templates: "Templates",
    integrations: "Integrations",
    nationalPipeline: "National pipeline",
    gsocReviews: "GSOC review queue",
    earlyAdvice: "Early advice",
    policyAgenda: "Policy agenda",
    delegatedAnalyses: "Delegated analyses",
    nationalGuidelines: "National guidelines",
    overlaps: "Overlaps and conflicts",
    cabinetAgenda: "Cabinet agenda",
    reglabOffice: "RegLab office",
    nationalSignals: "National signals",
    performance: "Performance and evaluation",
    conformanceAnalytics: "Conformance analytics",
    designSystem: "Design system",
    work: "Work",
    oversight: "Oversight",
    resources: "Resources",
    administration: "Administration",
    openConsole: "Open the Platform Console",
    sectionPolicy: "Policy work",
    sectionNational: "National oversight",
  },
  actions: {
    signIn: "Sign in",
    signOut: "Sign out",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    back: "Back",
    next: "Next",
    continue: "Continue",
    submit: "Submit",
    retry: "Try again",
    search: "Search",
    filter: "Filter",
    export: "Export",
    edit: "Edit",
    delete: "Delete",
    accept: "Accept",
    reject: "Reject",
    view: "View",
    open: "Open",
    create: "Create",
    run: "Run",
    markAllRead: "Mark all as read",
    viewAll: "View all",
    goHome: "Go to the home page",
    reload: "Reload the page",
    switchTenant: "Switch workspace",
    openAssistant: "Assistant",
    history: "History",
    file: "File",
  },
  states: {
    loading: "Loading…",
    saving: "Saving…",
    empty: "Nothing to show yet",
    comingSoon: "This view is being connected to the platform services.",
    notAvailable: "Not available",
    none: "None",
    yes: "Yes",
    no: "No",
    offline: "You are offline",
  },
  errors: {
    apiUnavailableTitle: "The platform service is not reachable",
    apiUnavailableBody: "We could not reach the National Policy AI service. Check your connection, or try again in a moment. If the problem continues, contact the platform support desk.",
    genericTitle: "Something went wrong",
    genericBody: "The request could not be completed. Try again; if it keeps happening, the platform team has been notified.",
    forbiddenTitle: "You do not have access to this page",
    forbiddenBody: "Your role in this workspace does not include this area. Ask your workspace administrator if you need access.",
    notFoundTitle: "Page not found",
    notFoundBody: "The address may be mistyped, or the page may have moved.",
    boundaryTitle: "This screen stopped working",
    boundaryBody: "An unexpected error occurred while showing this screen. Your saved work is not affected.",
    validation: "Some fields need attention.",
    conflict: "Someone else changed this item. Reload to see the latest version.",
    stepUpRequired: "This action requires you to confirm your identity again.",
    correlation: "Reference: {{id}}",
  },
  auth: {
    title: "Sign in to National Policy AI",
    subtitle: "The federal platform for developing, reviewing and evaluating public policy.",
    sso: "Sign in with UAE Government SSO",
    ssoHint: "Use your government account. Access is limited to authorised federal staff.",
    uaePass: "Sign in with UAE PASS",
    uaePassHint: "Use your verified UAE PASS account. You approve the sign-in in the UAE PASS app.",
    directory: "Sign in with {{name}}",
    gateTitle: "Sign in to {{name}}",
    gateSubtitle: "This is the ministry's own sign-in gate. It opens only this ministry's workspace.",
    gateUnknown: "This sign-in link does not belong to a workspace. Use the main sign-in below.",
    leaveGate: "Not from {{name}}? Use the main sign-in",
    thisWorkspace: "this workspace",
    notMember: "Your account is not a member of {{name}}. Ask the ministry's workspace administrator for an invitation, or use the main sign-in.",
    uaePassLevel: "Your UAE PASS account is not verified to the level needed for government staff. Upgrade it to a verified account in the UAE PASS app, then try again.",
    devTitle: "Development personas",
    devBadge: "DEV",
    devHint: "Available in development environments only. Choose a persona to sign in as that user.",
    signInAs: "Sign in as {{name}}",
    signingIn: "Signing in…",
    noMethods: "No sign-in method is configured for this environment. Contact the platform team.",
    sessionExpiredTitle: "Your session has ended",
    sessionExpiredBody: "For your security you have been signed out. Sign in again to continue; your saved work is kept.",
    signInAgain: "Sign in again",
    stepUp: "Identity confirmation",
    stepUpRecent: "Confirmed {{time}}",
    stepUpNone: "Not confirmed in this session",
    stepUpStart: "Confirm identity",
    stepUpCode: "Verification code",
    stepUpVerify: "Verify",
    profile: "My profile",
    roles: "Roles",
    workspace: "Workspace",
    workspaces: "Your workspaces",
    currentWorkspace: "Current workspace",
    defaultWorkspace: "Default",
    switched: "You are now working in {{name}}.",
    signedOut: "You have signed out.",
    notEnrolled: "Your government account is not enrolled on National Policy AI. Ask your workspace administrator for access.",
    ssoFailed: "Single sign-on did not complete. Try again; if it keeps failing, contact the platform team.",
    personasUnavailable: "Development personas could not be loaded.",
    heroEyebrow: "Federal policy platform",
    heroTitle: "Better policy, faster, with AI that shows its sources",
    features: {
      ai: "AI drafts every stage and cites the evidence behind it",
      guidebook: "Checks each policy against the Guidebook before review",
      bilingual: "Works natively in Arabic and English, end to end",
    },
  },
  notifications: {
    title: "Notifications",
    unread_one: "{{count}} unread notification",
    unread_other: "{{count}} unread notifications",
    empty: "You are all caught up.",
    emptyHint: "New tasks, decisions and AI proposals for you appear here.",
    markRead: "Mark as read",
    open: "Open notifications",
  },
  views: {
    label: "Saved views",
    save: "Save view",
    saveTitle: "Save this view",
    saveHint: "Keeps the current filters and sort so you can come back to them in one click.",
    name: "Name",
    namePlaceholder: "For example, my overdue policies",
    saved: "View “{{name}}” saved",
    remove: "Remove view “{{name}}”",
    removed: "View removed",
    showAs: "Show as",
    table: "Table",
    board: "Board",
  },
  autosave: {
    saved: "Draft saved {{time}}",
    hint: "Your answers are kept on this device until you finish.",
  },
  settingsNav: {
    label: "Settings sections",
  },
  palette: {
    label: "Command palette",
    placeholder: "Search pages and actions…",
    empty: "No matching page or action.",
    pages: "Pages",
    actions: "Actions",
    hint: "Press {{key}} to open the command palette",
    open: "Search",
    openLong: "Search pages and actions",
    askAi: "Ask the AI assistant",
  },
  assistant: {
    ask: "Ask AI",
    title: "AI assistant",
  },
  dictation: {
    start: "Dictate",
    stop: "Stop and transcribe",
    recording: "Recording {{time}}",
    transcribing: "Transcribing…",
    micBlocked: "The browser did not allow the microphone. Allow it for this site, then try again.",
    empty: "Nothing was heard in the recording.",
    note: "Your voice is transcribed on government compute and not kept. Check the text before you save.",
  },
  tenant: {
    ministry: "Ministry",
    oversight: "GSOC",
    platform: "Platform",
  },
  time: {
    justNow: "just now",
  },
  enums: {} as Record<string, Record<string, string>>,
};

export default en;
export type CommonCatalog = typeof en;
